import React, { useState, useEffect } from "react";
import "./styles/login.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../config";
import referralService from "../services/referralService";

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    referral_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referralValidation, setReferralValidation] = useState({
    isValid: null,
    referrerName: "",
    isValidating: false
  });

  // Check for referral code in URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
      validateReferralCode(refCode);
    }
  }, [location]);

  // Validate referral code when it changes
  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralValidation({ isValid: null, referrerName: "", isValidating: false });
      return;
    }

    setReferralValidation(prev => ({ ...prev, isValidating: true }));
    
    try {
      const response = await referralService.validateReferralCode(code);
      setReferralValidation({
        isValid: true,
        referrerName: response.data.referrer_name,
        isValidating: false
      });
    } catch (err) {
      setReferralValidation({
        isValid: false,
        referrerName: "",
        isValidating: false
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (error) setError(null);
    
    // Validate referral code with debouncing
    if (name === 'referral_code') {
      const timeoutId = setTimeout(() => {
        validateReferralCode(value);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    let responseData = null;

    // Basic validation
    if (formData.password !== formData.password_confirmation) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
          referral_code: formData.referral_code,
        }),
      });

      let responseData;
      try {
        responseData = await response.json();
        console.log("Registration response:", responseData);
      } catch (jsonError) {
        console.error("Error parsing JSON:", jsonError);
        throw new Error("Invalid response from server");
      }

      // Check if registration was successful based on the response data
      if (responseData.status === "success" || responseData.token) {
        if (responseData.token) {
          // Store only the bearer token format for API calls
          localStorage.setItem("bearerToken", `Bearer ${responseData.token}`);

          // Store user data
          if (responseData.user) {
            // Store individual user fields for easier access
            localStorage.setItem("userId", responseData.user.id);
            localStorage.setItem("userRole", responseData.user.role);
            localStorage.setItem("userName", responseData.user.name);
            localStorage.setItem("userEmail", responseData.user.email);
          }

          console.log("Stored user data:", {
            user: responseData.user,
          });

          // Redirect to dashboard or home
          navigate("/");
        } else {
          // If registration is successful but no token is returned, redirect to login
          alert("Registration successful! Please login to continue.");
          navigate("/login");
        }
      } else {
        // If registration failed, throw an error with the message from the server
        throw new Error(
          responseData.message || "Registration failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Registration error:", err);

      if (err.message.includes("Failed to fetch")) {
        setError(
          "Cannot connect to the server. Please check if the backend server is running and CORS is enabled."
        );
      } else if (responseData && responseData.errors) {
        // Handle validation errors
        const errorMessages = Object.values(responseData.errors).flat();
        setError(errorMessages.join(", "));
      } else if (responseData && responseData.message) {
        // Handle other server errors
        setError(responseData.message);
      } else {
        setError("Failed to register. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="login-container">
      <div className="login-content responsive-hide">
        <div className="secure-badge">
          <i className="fas fa-shield-alt"></i>
          <span>Secure Registration</span>
        </div>
        <h1>Join</h1>
        <h1 className="gradient-text">RoamBis</h1>
        <p>Create your account to access global connectivity</p>
        <p>and manage your eSIM plans from anywhere in the world.</p>
        <div className="login-list">
          <div className="login-list-item">
            <div className="list-item">
              <i className="fas fa-check"></i>
              <span>Instant access to global eSIM plans</span>
            </div>
            <div className="list-item">
              <i className="fas fa-check"></i>
              <span>Manage multiple devices</span>
            </div>
            <div className="list-item">
              <i className="fas fa-check"></i>
              <span>24/7 customer support</span>
            </div>
          </div>
        </div>
      </div>

      <div className="login-form">
        <h1>Sign Up</h1>
        <p>Create your RoamBis account</p>
        {error && <div className="error-message">{error}</div>}
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <div className="input-group">
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <i className="fas fa-user"></i>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <i className="fas fa-envelope"></i>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <i
                  className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  onClick={togglePasswordVisibility}
                  style={{ cursor: "pointer" }}
                ></i>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="password_confirmation">Confirm Password</label>
              <div className="input-group">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="password_confirmation"
                  name="password_confirmation"
                  placeholder="Confirm your password"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required
                />
                <i
                  className={`fas ${
                    showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                  }`}
                  onClick={toggleConfirmPasswordVisibility}
                  style={{ cursor: "pointer" }}
                ></i>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="referral_code">
                Referral Code 
                <span className="optional-label">(Optional)</span>
              </label>
              <div className="input-group">
                <input
                  type="text"
                  id="referral_code"
                  name="referral_code"
                  placeholder="Enter referral code"
                  value={formData.referral_code}
                  onChange={handleChange}
                />
                <i className={`fas ${
                  referralValidation.isValidating 
                    ? 'fa-spinner fa-spin' 
                    : referralValidation.isValid === true 
                      ? 'fa-check-circle' 
                      : referralValidation.isValid === false 
                        ? 'fa-times-circle' 
                        : 'fa-gift'
                }`}></i>
              </div>
              {referralValidation.isValid === true && (
                <div className="referral-success">
                  <i className="fas fa-check-circle"></i>
                  You'll be referred by {referralValidation.referrerName}
                </div>
              )}
              {referralValidation.isValid === false && formData.referral_code && (
                <div className="referral-error">
                  <i className="fas fa-exclamation-circle"></i>
                  Invalid referral code
                </div>
              )}
            </div>
            
            <button
              type="submit"
              className={`sign-in-button ${loading ? "loading" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Creating Account...
                </>
              ) : (
                <>
                  Sign Up <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
            <p className="signup-prompt">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Signup;
